// Module `stakeholders` (API-v3 10): people, organisations, lists, privacy,
// analytics, funder share links and the funder view. Owned by WP stakeholders.
import { z } from "zod";
import { EVENT_KINDS, GENDER_ANSWERS, REQUEST_KINDS, SECTORS, SHARE_AUDIENCES, type Sector } from "../enums";
import { LIMITS } from "../limits";
import { uuid } from "../schemas/common";
import {
  country,
  email,
  isoDate,
  text,
  textOrEmpty,
  textOrNull,
  type ActorRef,
  type FileRef,
  type FromRecord,
  type HistoryEntry,
  type Id,
  type Link,
  type Paged,
  type StatusChip,
  type Steps,
} from "./common";
import type { GuestContactField } from "./events";

export const sector = z.enum(SECTORS);
export type { Sector };

// ---------- tags (round 4: one shared list per organisation) ----------

export const TAG_ENTITY_TYPES = ["person", "organisation", "activity", "note"] as const;
export type TagEntityType = (typeof TAG_ENTITY_TYPES)[number];
export const TAG_ENTITY_LABELS: Record<TagEntityType, string> = { person: "People", organisation: "organisations", activity: "activities", note: "notes" };
export const TAG_COLOURS = ["blue", "purple", "teal", "green", "brown", "red", "grey"] as const;
export type TagColour = (typeof TAG_COLOURS)[number];
export const WHO_CREATES_TAGS = ["admins", "suggest", "everyone"] as const;
export type WhoCreatesTags = (typeof WHO_CREATES_TAGS)[number];
export const WHO_CREATES_TAGS_LABELS: Record<WhoCreatesTags, string> = {
  admins: "Only admins",
  suggest: "Everyone suggests, admins approve",
  everyone: "Everyone",
};

/** A tag as a chip on a record: the group gives the colour. */
export type TagChip = { id: Id; label: string; group: string | null; colour: TagColour; archived: boolean; suggested: boolean };

export const tagGroupBody = z.object({
  name: text(80, "Group name"),
  oneOrMany: z.enum(["one", "many"]),
  useInAnalytics: z.boolean(),
  appliesTo: z.array(z.enum(TAG_ENTITY_TYPES)).min(1, "Choose where the tags are used.").max(4),
  teamOnly: z.boolean(),
  colour: z.enum(TAG_COLOURS),
});
export type TagGroupBody = z.infer<typeof tagGroupBody>;
export const tagGroupPatch = tagGroupBody.partial();
export const newTagBody = z.object({ label: text(60, "Tag") });
export const tagPatch = z.object({
  label: text(60, "Tag").optional(),
  resultId: uuid.nullable().optional(),
  archived: z.boolean().optional(),
});
export const mergeTagBody = z.object({ intoTagId: uuid });
export const tagSettingsBody = z.object({ whoCreatesTags: z.enum(WHO_CREATES_TAGS) });
export const acceptSuggestionBody = z.object({ groupId: uuid });
export const useSuggestionBody = z.object({ tagId: uuid });
export const tagSettingsQuery = z.object({ projectId: uuid.optional() });

export type TagSettings = {
  whoCreatesTags: WhoCreatesTags;
  /** Owners and admins change the list; everyone else reads it. */
  canManage: boolean;
  figures: { groups: number; tagsInUse: number; suggested: number };
  suggestions: {
    id: Id;
    tagId: Id;
    label: string;
    suggestedBy: string | null;
    people: number;
    group: { id: Id; name: string } | null;
    /** An existing tag with nearly the same words ("data-sharing" looks like "Data sharing"). */
    looksLike: { id: Id; label: string } | null;
  }[];
  groups: {
    id: Id;
    name: string;
    oneOrMany: "one" | "many";
    useInAnalytics: boolean;
    appliesTo: TagEntityType[];
    teamOnly: boolean;
    colour: TagColour;
    /** "People, organisations, activities, notes", plus " · visible to the team only". */
    appliesText: string;
    tags: { id: Id; label: string; people: number; activities: number; resultId: Id | null; resultLabel: string | null; archived: boolean }[];
  }[];
  /** The results of the chosen project, for the Logframe column. */
  results: { id: Id; label: string }[];
};

/** For pickers: the live tags of the groups that apply to one kind of record. */
export type TagOptions = {
  groups: { id: Id; name: string; oneOrMany: "one" | "many"; colour: TagColour; tags: { id: Id; label: string }[] }[];
  /** "create": a new tag is added at once; "suggest": it waits for an admin; "none": pick only. */
  canCreate: "create" | "suggest" | "none";
};
export const tagOptionsQuery = z.object({ for: z.enum(TAG_ENTITY_TYPES).default("person") });
export const newTagInPickerBody = z.object({ label: text(60, "Tag"), groupId: uuid });
export const setTagsBody = z.object({ tagIds: z.array(uuid).max(40) });

// ---------- 10.1 people and organisations ----------

export type StakeholderSummary = {
  people: number;
  organisations: number;
  womenPct: number | null;
  newThisQuarter: number;
  lists: number;
  /** Round 4 attention cards (team only; 0 for partners). */
  followUpsThisWeek?: number;
  quietSixMonths?: number;
  possibleDuplicates?: number;
  /** EV-F1 (round 3 feedback, staff editors): people whose RSVP or speaker answers differ from their record, waiting for "Review changes". */
  toReview?: { count: number; people: { name: string; path: string; via: string }[] };
};
export const ENGAGEMENT_FILTERS = ["active_3m", "quiet_6m", "new_quarter", "follow_up_due"] as const;
export type EngagementFilter = (typeof ENGAGEMENT_FILTERS)[number];
export const ENGAGEMENT_FILTER_LABELS: Record<EngagementFilter, string> = {
  active_3m: "Active in the last 3 months",
  quiet_6m: "Quiet for 6+ months",
  new_quarter: "New this quarter",
  follow_up_due: "Follow-up due",
};
export const PEOPLE_SORTS = ["name", "country", "last_contact"] as const;
const manyIds = (max: number) => z.preprocess((v) => (v === undefined || v === "" ? undefined : Array.isArray(v) ? v : String(v).split(",")), z.array(uuid).max(max).optional());
export const peopleQuery = z.object({
  q: z.string().trim().max(LIMITS.searchQuery).optional(),
  country: z.string().trim().toUpperCase().length(2).optional(),
  sector: sector.optional(),
  /** Tag ids (comma separated): people with all of them. */
  tag: manyIds(20),
  engagement: z.enum(ENGAGEMENT_FILTERS).optional(),
  /** The people of one organisation ("All 14" on its record). */
  org: uuid.optional(),
  /** The people of one list ("Export (Excel)" of a list). */
  list: uuid.optional(),
  sort: z.enum(PEOPLE_SORTS).optional(),
  dir: z.enum(["asc", "desc"]).optional(),
  /** Export only: these people. */
  ids: manyIds(500),
  cursor: z.string().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(LIMITS.pageMax).default(LIMITS.pageDefault),
});
export type PersonRow = {
  id: Id;
  name: string;
  position: string | null;
  organisation: string | null;
  country: string | null;
  sector: string | null;
  activities: number;
  lastContact: string | null;
  path: string;
  /** The earliest open follow-up about the person (team only; null for partners). */
  nextStep: { title: string; dueOn: string | null; path: string } | null;
  /** Round 4: the person's tags (team-only groups left out for partners). */
  tags?: TagChip[];
};
/**
 * A follow-up about a person: a to-do linked to them (mission and event
 * follow-ups, Logistics) or a meeting's next step with them in "Who".
 * Internal: the team sees it, partners and the person never do.
 */
export type FollowUpAbout = {
  id: string;
  kind: "todo" | "meeting_action";
  title: string;
  /** "M1.2 Mission to Tirana" or "Meeting: Kick-off, 3 Oct". */
  source: string;
  path: string;
  owner: string | null;
  dueOn: string | null;
  overdue: boolean;
  done: boolean;
  doneAt: string | null;
};
export type PeoplePage = Paged<PersonRow> & { total: number };
export const personBody = z.object({
  name: text(LIMITS.userName, "Name"),
  position: textOrNull(LIMITS.positionTitle),
  stakeholderOrgId: uuid.nullable(),
  newOrgName: textOrNull(LIMITS.stakeholderOrgName),
  country: country.nullable(),
  email: email.nullable(),
  gender: z.enum(GENDER_ANSWERS).nullable(),
  sector: sector.nullable(),
  /** Tags from the organisation's list (Settings › Tags). Omitted on update: unchanged. */
  tagIds: z.array(uuid).max(40).optional(),
  consent: z.boolean(),
  phone: textOrNull(LIMITS.phone).optional(),
  languages: z.array(z.string().trim().min(1).max(40)).max(20).optional(),
  /** Relationship owner (round 4): a team member of the project; staff only. */
  ownerId: uuid.nullable().optional(),
  /** Add to these hand-picked lists of the project (round 4 form, "Add to lists"). */
  listIds: z.array(uuid).max(20).optional(),
  /** Speaker details (P5): a title such as "Dr", a short biography and a photo (an uploaded file id, purpose "photo"). */
  title: textOrNull(LIMITS.personTitle, "Title").optional(),
  bio: textOrNull(LIMITS.personBio, "Biography").optional(),
  photoFileId: uuid.nullable().optional(),
  /** EV-F5 (round 3 feedback): the event profile kept for the next invitation. Omitted on update: unchanged. */
  dietary: textOrEmpty(500).optional(),
  accessNeeds: textOrEmpty(500).optional(),
  travelOrigin: textOrEmpty(120).optional(),
});
export type PersonBody = z.infer<typeof personBody>;
export const personPatch = personBody.partial();
/**
 * POST a new person. ST-N3/ST-N7: mergeIntoId = "Merge instead": the details
 * typed go into this existing record (empty fields filled, tags, lists and
 * consent moved), as "Merge with…" does; no second record stays. Team only.
 */
export const createPersonBody = personBody.extend({ mergeIntoId: uuid.optional() });
export type CreatePersonBody = z.infer<typeof createPersonBody>;
/** A match in the people look-up (GET /stakeholders/lookup/people): enough to tell two "Ana Petrova"s apart (ST-N8). */
export type PersonLookupItem = { id: string; name: string; organisation: string | null; email: string | null; country: string | null; activities: number };
/** 409 DUPLICATE_EMAIL details: the person who has the address, when the caller may see them (ST-N3). */
export type DuplicateEmailDetails = { existing: { id: string; name: string } | null };
/**
 * ST-N6: the same real person in another project of the organisation (same
 * email; without email, the same name at an organisation of the same name).
 * Read-only, team only; nothing else of the other project shows.
 */
export type AlsoInContactWith = {
  project: string;
  lastContact: { on: string; kind: string | null } | null;
  contactPerson: string | null;
  /** "Also in contact with Green Ports – last contact 12 Oct 2026 (meeting), contact person: Laura Jansen." */
  text: string;
};
export type PersonDetail = {
  id: Id;
  name: string;
  titleLine: string;
  status: StatusChip;
  engagement: { code: string | null; activity: string; date: string | null; path: string | null; role: string; response: StatusChip }[];
  whatWasSaid: { text: string; source: string; loggedBy: string; at: string; path: string | null }[];
  /** Open follow-ups about the person, by due date (team only; empty for partners). Done ones are in whatWasSaid. */
  openFollowUps: FollowUpAbout[];
  details: {
    email: string | null;
    phone: string | null;
    gender: string | null;
    languages: string[];
    tags: TagChip[];
    consent: { text: string; provenance: FromRecord | null; file: FileRef | null } | null;
    /** Speaker details (P5). */
    title: string | null;
    bio: string | null;
    photo: FileRef | null;
  };
  history: HistoryEntry[];
  form: PersonBody;
  canEdit: boolean;
  /** Optional (WP stakeholders): the person's organisation record. */
  organisation?: Link | null;
  /** Additive: their "Your information" link works now; staff can cancel it (POST …/self-link/cancel). */
  selfLinkActive?: boolean;
  /** Additive: "Doesn't want to be contacted (since 2 Oct 2026)", said on their own page; null otherwise. */
  noContact?: string | null;
  /** ST-N6: "Also in contact with" lines (team only; empty for partners). */
  alsoInContactWith?: AlsoInContactWith[];
  // ----- round 4 (wireframe "Stakeholder") -----
  /** Role chips next to the tags ("Speaker"). */
  roles?: string[];
  figures?: {
    activities: number;
    lastContact: { on: string; what: string | null } | null;
    /** Team only (null for partners). */
    nextFollowUp: { dueOn: string | null; title: string } | null;
    /** Team only (null for partners). */
    owner: { id: Id; name: string } | null;
  };
  timeline?: TimelineItem[];
  /** Open and done follow-ups (team only). */
  followUps?: FollowUpAbout[];
  orgCard?: { label: string; path: string; sub: string; people: { name: string; position: string | null; path: string }[] } | null;
  lists?: Link[];
  /** What the person shared themselves (their RSVP): via, photos, slides. */
  sharedByPerson?: { via: string; photos: string | null; slides: string | null } | null;
  /** Staff who may add notes and set the owner. */
  canAddNote?: boolean;
  /** Team members to choose the relationship owner from (staff editors only). */
  ownerOptions?: { id: Id; name: string }[];
  /** "her" / "him" / "them" for the wireframe's words ("Shared by her"). */
  pronoun?: "her" | "him" | "them";
  /** The project's activities a note can be about (staff editors only). */
  activityOptions?: { id: Id; code: string; title: string }[];
  /** EV-F5 (round 3 feedback): the event profile (what the person gave at events, kept for the next one). */
  eventProfile?: {
    items: { label: string; value: string }[];
    /** "Last confirmed: 1 Nov 2026 via RSVP A2.7"; null when the person never confirmed their details themselves. */
    lastConfirmed: string | null;
  };
  /** EV-F1 (staff editors only): answers from events that differ from this record, to accept or keep out. */
  review?: RecordReviewItem[];
};
/** EV-F1: one answer (an RSVP or a speaker form) whose details differ from the record. */
export type RecordReviewItem = {
  registrationId: Id;
  /** "RSVP · A2.7 Regional workshop · 1 Nov 2026" */
  via: string;
  eventPath: string;
  fields: { field: GuestContactField; label: string; onRecord: string | null; atEvent: string }[];
};

export const TIMELINE_FILTERS = ["all", "notes", "meetings_calls", "activities", "emails"] as const;
export type TimelineFilter = (typeof TIMELINE_FILTERS)[number];
/** One line of a person's timeline: a note from the team, an activity event, an email sent, a follow-up done. */
export type TimelineItem = {
  id: string;
  kind: "note" | "meeting" | "call" | "email" | "activity" | "email_sent" | "follow_up";
  /** Which timeline filter shows it. */
  filter: Exclude<TimelineFilter, "all">;
  title: string;
  /** ISO time it happened. */
  at: string;
  by: string | null;
  text: string | null;
  link: Link | null;
  pinned: boolean;
  /** "Only me": shown to its author only. */
  private: boolean;
  /** A note the team wrote here (pin, delete own). */
  noteId: string | null;
  canPin: boolean;
  canDelete: boolean;
  followUp: { title: string; dueOn: string | null } | null;
  /** Tags on a team note. */
  tags?: TagChip[];
  /** Dated after today (a meeting still to come): shown under "Coming up", not in the history. */
  upcoming?: boolean;
};
export const NOTE_KINDS = ["note", "meeting", "call", "email"] as const;
export type NoteKind = (typeof NOTE_KINDS)[number];
export const NOTE_KIND_LABELS: Record<NoteKind, string> = { note: "Note", meeting: "Meeting", call: "Call", email: "Email" };
export const noteBody = z.object({
  kind: z.enum(NOTE_KINDS),
  text: text(5_000, "Note"),
  activityId: uuid.nullable(),
  visibility: z.enum(["team", "private"]),
  happenedOn: isoDate.nullable().optional(),
  followUp: z.object({ title: textOrNull(500, "Follow-up"), dueOn: isoDate }).nullable(),
  /** Tags of groups used on notes. */
  tagIds: z.array(uuid).max(20).optional(),
});
export type NoteBody = z.infer<typeof noteBody>;
export const notePatch = z.object({ pinned: z.boolean() });
export const followUpPatch = z.object({ done: z.boolean() });
export const bulkTagBody = z.object({ contactIds: z.array(uuid).min(1).max(5_000), tagId: uuid });
/**
 * ST-N6 bulk delete (People list, project admins): up to 1,000 people of this
 * project. `confirmCount` is the number the admin typed to confirm; it must be
 * the number of people ticked.
 */
export const bulkDeleteBody = z.object({ contactIds: z.array(uuid).min(1).max(1_000), confirmCount: z.number().int() });
export type BulkDeleteResult = { deleted: number };
/** ST-N6: an import into this project ("Undo this import" within 30 days). */
export type ContactImportSummary = {
  id: Id;
  fileName: string;
  at: string;
  by: string | null;
  peopleCreated: number;
  peopleUpdated: number;
  orgsCreated: number;
  undoneAt: string | null;
  /** Until when "Undo this import" is offered (30 days); null once undone. */
  undoUntil: string | null;
  canUndo: boolean;
};
/** What "Undo this import" did; people used since the import (a note, a meeting, an event, an edit) are kept. */
export type ImportUndoResult = {
  peopleRemoved: number;
  peopleKept: number;
  organisationsRemoved: number;
  organisationsKept: number;
  fieldsRestored: number;
  listEntriesRemoved: number;
  tagsRemoved: number;
  notesRemoved: number;
};
export type DuplicatePair = {
  a: { id: Id; name: string; sub: string | null; path: string };
  b: { id: Id; name: string; sub: string | null; path: string };
  reason: "Same email" | "Same name and organisation" | "Same name" | "Same short name";
};
export const mergePeopleBody = z.object({ otherId: uuid });
/** ST1: "Merge" on an organisation: `otherId` goes into the organisation of the path. */
export const mergeOrgsBody = z.object({ otherId: uuid });
/** GET /stakeholders/duplicates: people, and (ST1) organisations. */
export type Duplicates = { pairs: DuplicatePair[]; organisations?: DuplicatePair[] };
export type OrgRow = {
  id: Id;
  name: string;
  country: string | null;
  type: string | null;
  people: number;
  activities: number;
  path: string;
  // round 4 (wireframe "Organisations")
  tags?: TagChip[];
  role?: string | null;
  lastContact?: string | null;
  logoUrl?: string | null;
};
export const ORG_SORTS = ["name", "country", "people", "activities", "last_contact"] as const;
export const orgListQuery = z.object({
  q: z.string().trim().max(100).optional(),
  sector: sector.optional(),
  country: z.string().trim().toUpperCase().length(2).optional(),
  sort: z.enum(ORG_SORTS).optional(),
  dir: z.enum(["asc", "desc"]).optional(),
  cursor: z.string().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(500).default(200),
});
export const ORG_KINDS = ["National ministry", "Agency", "Local authority", "NGO", "Think tank", "University", "Company", "Network or association", "International body"] as const;
export const ORG_ROLES = ["Beneficiary", "Partner", "Stakeholder", "Funder", "Supplier"] as const;
export const orgBody = z.object({
  name: text(LIMITS.stakeholderOrgName, "Name"),
  type: sector.nullable(),
  subtype: textOrNull(80),
  country: country.nullable(),
  website: z.string().trim().url().max(LIMITS.website).nullable(),
  address: textOrNull(500),
  /** Tags from the organisation's list (Settings › Tags). Omitted on update: unchanged. */
  tagIds: z.array(uuid).max(40).optional(),
  relationshipStatus: textOrNull(80),
  // round 4 (wireframe "Add Organisation")
  shortName: textOrNull(60, "Short name").optional(),
  generalEmail: email.nullable().optional(),
  logoFileId: uuid.nullable().optional(),
  mainContactId: uuid.nullable().optional(),
  /** People already in Stakeholders who work here: they are linked to it. */
  linkContactIds: z.array(uuid).max(200).optional(),
  /** Hand-picked lists to put the organisation's people on. */
  listIds: z.array(uuid).max(20).optional(),
});
export type OrgBody = z.infer<typeof orgBody>;
export const orgPatch = orgBody.partial();
export type OrgDetail = {
  id: Id;
  name: string;
  subtitle: string;
  tiles: { people: number; activities: number; womenPct: number | null };
  people: { id: Id; name: string; position: string | null; activities: number; lastContact: string | null; path: string }[];
  /** Round 4: the full count behind "All 14" (people above are the first ones). */
  peopleTotal?: number;
  activities: { code: string | null; activity: string; sub: string; path: string | null; people: number; status?: "coming_up" | "done" | null }[];
  details: OrgBody;
  /** The organisation's tags (team-only groups left out for partners). */
  tags?: TagChip[];
  history: HistoryEntry[];
  canEdit: boolean;
  // round 4 (wireframe "Organisation Record")
  logoUrl?: string | null;
  figures?: { people: number; activities: number; participants: number; participantsWomenPct: number | null; lastContact: { on: string; who: string | null } | null };
  /** Notes about the organisation and the team's notes on its people (team only). */
  notes?: TimelineItem[];
  mainContact?: { name: string; position: string | null; path: string; owner: string | null } | null;
  lists?: Link[];
  canAddNote?: boolean;
  /** People of the organisation for "Main contact" (editors). */
  peopleOptions?: { id: Id; name: string }[];
  logo?: FileRef | null;
};
export const lookupQuery = z.object({ q: z.string().trim().max(LIMITS.searchQuery).default("") });
export type PeopleLookup = { items: { id: Id; name: string; organisation: string | null }[] };
export type OrgLookup = { items: { id: Id; name: string; country: string | null }[] };

/** "firstName" and "lastName" are joined into the name when no column holds the full name. */
export const CONTACT_FIELDS = ["name", "firstName", "lastName", "organisation", "email", "gender", "position", "country", "sector", "tags", "phone", "notes"] as const;
export type ContactField = (typeof CONTACT_FIELDS)[number];
export const contactMapping = z.record(z.string().max(200), z.enum([...CONTACT_FIELDS, "ignore"]));
export type ContactImportPreview = {
  previewId: Id;
  fileName: string;
  rows: number;
  columns: { header: string; sample: string[]; field: ContactField | "ignore" }[];
  /** ST2: "matched" counts existing records only; a row repeating another row of the file is "twiceInFile". */
  counts: { newPeople: number; matched: number; twiceInFile?: number; withoutEmail: number; newOrganisations: number };
  /**
   * ST2: what the file would change on matched records, old -> new per field.
   * `chosen` is what the import does now: by default only empty fields are
   * filled; the user ticks the others (commit body `apply`).
   */
  changes?: { contactId: Id; name: string; fields: { field: ContactUpdateField; label: string; old: string | null; new: string; empty: boolean; chosen: boolean }[] }[];
  /** ST3: organisations the file has for several countries: one organisation (default), or one per country (`splitByCountry`). */
  multiCountry?: { key: string; name: string; countries: string[]; split: boolean; /** ST-N4: people of the file per country, most first. */ people?: { country: string; count: number }[] }[];
  /** ST-N5: each value of the gender column and how it is read ("Male" → "Man"; null = left empty); `inferred`: marked as guessed. */
  genderValues?: { value: string; rows: number; readAs: string | null; inferred: boolean }[];
  /** Values the import cannot use, such as "Country not recognised: Narnia (2 rows)…". */
  warnings?: string[];
  /** What the import does that may surprise, such as one organisation per country. */
  notices?: string[];
  /** Round 4 "See the matches": who each matched row is (the first 200). */
  matches?: { name: string; email: string | null; organisation: string | null; matchedTo: string; by: "Same email" | "Same name and organisation" | "Twice in the file" | "Possible match, added as new" }[];
};
export const contactImportPreviewBody = z.object({ fileId: uuid });
export const CONTACT_UPDATE_FIELDS = ["email", "position", "organisation", "country", "gender", "phone"] as const;
export type ContactUpdateField = (typeof CONTACT_UPDATE_FIELDS)[number];
export const contactImportMappingBody = z.object({
  mapping: contactMapping,
  /** ST2: per matched record, the fields to take from the file (the others stay). Without it only empty fields are filled. */
  apply: z
    .array(z.object({ contactId: uuid, fields: z.array(z.enum(CONTACT_UPDATE_FIELDS)).max(CONTACT_UPDATE_FIELDS.length) }))
    .max(20_000)
    .optional(),
  /** ST3: organisation names (the `key` of multiCountry) to make one per country. Left out: the default, one per country for generic names such as "Ministry of Foreign Affairs" (ST-N4). */
  splitByCountry: z.array(z.string().max(200)).max(200).optional(),
  /** ST-N5: a gender marked "(inferred)": left empty (default) or imported as given. */
  inferredGender: z.enum(["empty", "as_given"]).optional(),
  /** Round 4, "Tag and group them": tags for everyone in the file. */
  tagIds: z.array(uuid).max(20).optional(),
  /** A hand-picked list of the project to put everyone in, or a new one by this name. */
  listId: uuid.nullable().optional(),
  newListName: textOrNull(200, "List name").optional(),
});
export type ContactImportResult = { created: number; matched: number; twiceInFile?: number; skipped: number; orgsCreated: number; listId?: string | null; /** ST-N6: the import, for "Undo this import". */ importId?: string };

// ---------- 10.2 lists ----------

export const LIST_INVITATION_FILTERS = ["invited", "confirmed", "declined", "attended"] as const;
export const LIST_INVITATION_LABELS: Record<(typeof LIST_INVITATION_FILTERS)[number], string> = { invited: "Invited", confirmed: "Confirmed", declined: "Declined", attended: "Attended" };
export const listRule = z.object({
  sector: sector.nullable(),
  country: country.nullable(),
  tagId: uuid.nullable(),
  // round 4 "richer rules" (optional: older rules have none of them)
  engagement: z.enum(ENGAGEMENT_FILTERS).nullable().optional(),
  /** People on the guest list of this activity, with this answer (any answer when null). */
  activityId: uuid.nullable().optional(),
  invitation: z.enum(LIST_INVITATION_FILTERS).nullable().optional(),
  /** Only people who agreed to hear from the project (consent on file, not withdrawn). */
  consentToNews: z.boolean().optional(),
});
export type ListRule = z.infer<typeof listRule>;
export const LIST_COMBINE_OPS = ["either", "both", "first_not_second"] as const;
export const LIST_COMBINE_LABELS: Record<(typeof LIST_COMBINE_OPS)[number], string> = {
  either: "People on either list",
  both: "People on both lists",
  first_not_second: "On the first list but not the second",
};
export const listCombine = z.object({ first: uuid, second: uuid, op: z.enum(LIST_COMBINE_OPS) });
export type ListCombine = z.infer<typeof listCombine>;
export const LIST_KIND_LABELS = { fixed: "Hand-picked", rule: "Updates itself", combined: "Combined" } as const;
export const listBody = z.object({
  name: text(200, "List name"),
  kind: z.enum(["rule", "fixed", "combined"]),
  rule: listRule.nullable(),
  contactIds: z.array(uuid).max(5_000),
  combine: listCombine.nullable().optional(),
  description: textOrEmpty(1_000).optional(),
  shared: z.boolean().optional(),
});
export type ListBody = z.infer<typeof listBody>;
export const listPatch = listBody.partial();
export type ListRow = {
  id: Id;
  name: string;
  people: number;
  rule: string;
  usedIn: string[];
  path: string;
  kind?: "rule" | "fixed" | "combined";
  /** Round 4: who made it, "Team" when shared, or where it comes from ("From A2.1"). */
  owner?: string | null;
  shared?: boolean;
  description?: string;
};
export type ListEmailStatus = "can_email" | "no_email" | "unsubscribed" | "bounced";
export const LIST_EMAIL_STATUS_LABELS: Record<ListEmailStatus, string> = { can_email: "Can be emailed", no_email: "No email", unsubscribed: "Unsubscribed", bounced: "Bounced" };
export type ListEmails = { emails: string[]; leftOut: { noEmail: number; unsubscribed: number; bounced: number } };
export type ListDetail = {
  id: Id;
  name: string;
  kind: "rule" | "fixed" | "combined";
  ruleText: string;
  people: number;
  members: Paged<{
    id: Id;
    name: string;
    position: string | null;
    organisation: string | null;
    country: string | null;
    lastContact: string | null;
    path: string;
    /** Round 4. */
    email?: string | null;
    emailStatus?: ListEmailStatus;
  }> & {
    total: number;
  };
  usedIn: Link[];
  form: ListBody;
  // round 4
  description?: string;
  owner?: string | null;
  shared?: boolean;
  /** Only the person who made it may change who sees it, rename or delete a list that is not shared. */
  canEdit?: boolean;
};
export const listPreviewBody = z.object({ rule: listRule });
export const listMembersBody = z.object({ add: z.array(uuid).max(5_000), remove: z.array(uuid).max(5_000) });

// ---------- 10.3 data and privacy ----------

export type PrivacyOverview = {
  tiles: { consentOnFile: number; consentPct: number; /** "12% of people", "<1% of people" (never "0%" next to a count above 0). */ consentShare: string; expiring90: number; renewalReady: boolean; openRequests: number; deletedThisYear: number };
  requests: {
    id: Id;
    receivedAt: string;
    from: string;
    fromPath: string | null;
    organisation: string | null;
    asksFor: string;
    due: string;
    status: StatusChip;
    path: string;
  }[];
  accessLog: { who: string; action: string; count: number; at: string; context?: string }[];
  /** Optional (WP stakeholders): consents expiring within 90 days whose renewal email was not sent yet. */
  renewalsToSend?: number;
};
export const requestKind = z.enum(REQUEST_KINDS);
export const newDataRequestBody = z.object({
  contactId: uuid.nullable(),
  requesterName: text(LIMITS.userName, "Name"),
  requesterEmail: email,
  kind: requestKind,
  receivedAt: isoDate,
});
export const dataRequestPatch = z.object({
  step: z.enum(["identity_checked", "prepared"]).optional(),
  identityFileId: uuid.nullable().optional(),
  contactId: uuid.optional(),
});
export type DataRequest = {
  id: Id;
  requester: string;
  kind: string;
  status: StatusChip;
  receivedAt: string;
  dueAt: string;
  steps: Steps;
  holdings: { data: string; where: string; source: string }[];
  identityFile: FileRef | null;
  identityNeeded: boolean;
  person: Link | null;
  closedAt: string | null;
  // Optional (WP stakeholders):
  kindKey?: "access" | "deletion" | "correction";
  stepKey?: "received" | "identity_checked" | "prepared" | "sent" | "erased" | "done";
  requesterEmail?: string;
  message?: string;
  via?: "staff" | "form";
  exportReady?: boolean;
  /**
   * ST8: the address the request came from against the person's record:
   * "same", "different" (the record has another address) or "none" (the
   * record has no address). Anything but "same" is flagged.
   */
  emailCheck?: { result: "same" | "different" | "none"; recordEmail: string | null };
  /** ST8: deleting is possible only once the identity is checked and the data prepared. */
  canErase?: boolean;
  /** Sending a copy needs the identity checked first. */
  canSendCopy?: boolean;
};
export const publicPrivacyRequestBody = z.object({
  name: text(LIMITS.userName, "Name"),
  email,
  kind: requestKind,
  orgSlug: z.string().trim().min(1).max(80),
  message: textOrEmpty(4_000),
});

// ---------- "Your information" (the person's own page, no account) ----------
//
// Marius, event space 9: a person can withdraw only what they shared
// themselves (their RSVP answers, speaker bio, photo, slides, consents). They
// never see what the organisation records about them.

/** What a person withdrew on one guest row, with the time (ISO). */
export type SelfWithdrawals = Partial<Record<"photoConsent" | "slidesShareConsent" | "answers" | "speakerDetails", string>>;
/** Per optional value of a guest row: given by the person (true) or entered by staff (false). */
export type SelfGiven = Partial<Record<"gender" | "dietary" | "accessNeeds" | "answers" | "slides", boolean>>;

export const SELF_WITHDRAWALS = ["contact_consent", "photo_consent", "slides_consent", "answers", "speaker_details"] as const;
export type SelfWithdrawal = (typeof SELF_WITHDRAWALS)[number];
export const selfWithdrawBody = z
  .object({ what: z.enum(SELF_WITHDRAWALS), registrationId: uuid.nullable().optional() })
  .refine((v) => v.what === "contact_consent" || !!v.registrationId, { message: "Choose the event.", path: ["registrationId"] });
export type SelfWithdrawBody = z.infer<typeof selfWithdrawBody>;
export const selfDeleteRequestBody = z.object({ message: textOrEmpty(4_000).optional() });

export type YourInformationEvent = {
  registrationId: Id;
  title: string;
  when: string;
  /** "Speaker", "Participant" … */
  role: string;
  /** The event is closed or over: answers can no longer be changed, but withdrawing still works. */
  closed: boolean;
  /** Their RSVP answers, as they gave them (labels ready to show). */
  answers: { label: string; value: string }[];
  /** True when there is an optional answer to remove (gender, dietary, accessibility, own answers). */
  hasOptionalAnswers: boolean;
  photoConsent: boolean;
  /** Speakers and moderators: what they sent on their speaker form; null for everyone else or when nothing is left. */
  speaker: { title: string | null; bio: string | null; hasPhoto: boolean; slides: string | null; slidesShareConsent: boolean; /** Fix round 1: their answers to the questions staff added. */ answers?: { label: string; value: string }[] } | null;
  /** "You withdrew your photo consent on 2 Oct 2026." */
  withdrawn: string[];
};
export type YourInformation = {
  firstName: string;
  organisation: string;
  consent: { onFile: boolean; text: string };
  events: YourInformationEvent[];
  /** An open request to delete everything, sent from this page or otherwise. */
  deletionRequested: { on: string } | null;
};

// ---------- 10.4 analytics ----------

export const analyticsQuery = z.object({
  period: z.string().regex(/^(whole|rp-\d{1,2}|year-\d{1,2}|last-3m)$/).default("whole"),
  // A1 (Marius's wireframe, 30 Oct): filters that apply to every view.
  result: z.string().uuid().optional(),
  country: z.string().regex(/^[A-Z]{2}$/).optional(),
  kind: z.enum(EVENT_KINDS).optional(),
});
/** A2 (Marius's wireframe, 30 Oct): a saved view of the Analytics page, shared with the project's team. query: the page's address after "?". */
export const analyticsViewBody = z
  .object({
    name: z.string().trim().min(1, "Name the view.").max(80, "Use at most 80 characters."),
    query: z.string().max(500).regex(/^[A-Za-z0-9=&_-]*$/, "This is not a view of the Analytics page."),
  })
  .strict();
export type AnalyticsViewBody = z.infer<typeof analyticsViewBody>;
export type AnalyticsSavedView = { id: string; name: string; query: string; by: string; createdAt: string; canDelete: boolean };
/** A9/A10 (Marius's wireframe, 30 Oct): the project's target share per stakeholder type group (no key = no target) and its partner countries. */
export const analyticsTargetsBody = z
  .object({
    typeTargetPcts: z.record(z.enum(["government", "civil_society", "academia", "media_other"]), z.number({ invalid_type_error: "Enter a percentage from 0 to 100." }).int("Enter a whole number.").min(0, "Enter a percentage from 0 to 100.").max(100, "Enter a percentage from 0 to 100.")),
    targetCountries: z.array(z.string().regex(/^[A-Z]{2}$/, "Pick a country from the list.")).max(60),
  })
  .strict();
export type AnalyticsTargets = z.infer<typeof analyticsTargetsBody>;
/** Round 5 (EV6, AN3): the project's target share of women at its events (one project-level default). */
export const womenTargetBody = z.object({
  womenTargetPct: z.number({ invalid_type_error: "Enter a percentage from 0 to 100." }).int("Enter a whole number.").min(0, "Enter a percentage from 0 to 100.").max(100, "Enter a percentage from 0 to 100."),
}).strict();
export type WomenTargetBody = z.infer<typeof womenTargetBody>;
/** AN6: the people behind a figure of the Analytics page (a returning row adds its country or organisation). */
export const analyticsPeopleQuery = analyticsQuery.extend({
  figure: z.enum(["engaged", "women", "returning", "quiet"]),
  // The page's filters (result, country, kind) apply as on Analytics; rowCountry is a row of
  // "Who keeps coming back" or the map (none = country not recorded), within them.
  rowCountry: z.string().regex(/^([A-Z]{2}|none)$/).optional(),
  organisation: z.string().uuid().optional(),
  // AN-F3: the people of one month (a bar of "Participation over time"), within the period.
  month: z.string().regex(/^\d{4}-\d{2}$/).optional(),
});
export const analyticsExportQuery = analyticsQuery.extend({
  what: z.enum(["all", "participants", "indicators"]).default("all"),
  format: z.enum(["xlsx", "csv", "pdf"]).default("xlsx"),
});
export type AnalyticsV3 = {
  period: string;
  /** Optional (WP stakeholders): the label of the chosen period. */
  periodLabel?: string;
  periods: { value: string; label: string }[];
  tiles: {
    participants: number;
    womenPct: number | null;
    stakeholders: number;
    newThisQuarter: number;
    expertDaysLogged: number;
    expertDaysContracted: number;
    deliverablesApproved: number;
    deliverablesTotal: number;
    publicationViews: number;
  };
  /** Groups under 5 are merged ("Other countries") or, with fewer than 5 in all, one hidden row (count and pct null). */
  byCountry: { label: string; count: number | null; pct: number | null; hidden?: boolean }[];
  indicators: { code: string; name: string; shortName: string | null; target: string; current: string; status: StatusChip }[];
  /**
   * Women, men, everyone else (non-binary, self-described, prefer not to say) and, on the team's own page,
   * "Not recorded" on its own (AN2); exports merge the last two into "Everyone else" (the under-5 rule).
   */
  byGender: { label: "Women" | "Men" | "Everyone else" | "Not recorded" | "All groups together"; pct: number | null; hidden?: boolean }[];
  byType: { label: string; pct: number | null; hidden?: boolean }[];
  /** "Groups under 5 are not shown separately…" when a group was merged or hidden. */
  smallGroupsNote?: string | null;
  feedback: { responses: number; ofParticipants: number; usefulPct: number | null; attendAgainPct: number | null };
};

// ---------- 10.5 funder share links and funder view ----------

export const shareAudience = z.enum(SHARE_AUDIENCES);
export const newShareLinkBody = z.object({
  audience: shareAudience,
  label: text(120, "Label"),
  expiresAt: isoDate.nullable(),
  emailTo: email.nullable(),
});
export type ShareLink = {
  id: Id;
  audience: "funder" | "auditor";
  label: string;
  createdBy: ActorRef;
  createdAt: string;
  expiresAt: string | null;
  revokedAt: string | null;
  lastViewedAt: string | null;
  views: number;
};
export type FunderView = {
  project: string;
  line: string;
  viewerLabel: string;
  notice: string;
  /** indicatorsFilled (AD1): "5 of 12", the indicators that have a figure (a reading or a count from the record). */
  tiles: { spent: string; spentPct: number | null; activitiesDone: string; participants: string; indicatorsOnTrack: string; indicatorsFilled?: string };
  waiting: { item: string; sent: string; waitingDays: number }[];
  /**
   * AU-F5 (Marius, 1 Nov): what the auditor needs to check an indicator: the
   * baseline with its year, the latest reading's date, the status, the source
   * of verification, and whether lower is better.
   */
  indicators: {
    name: string;
    target: string;
    current: string;
    baseline?: string;
    readingDate?: string | null;
    status?: { label: string; tone: string } | null;
    source?: string;
    lowerIsBetter?: boolean;
  }[];
  /** AU-F5: dates with the year; `docs` (auditor links only): the report as sent and the funder's acceptance. */
  reports: { title: string; status: string; docs?: AuditDoc[] }[];
  evidence: { note: string; files: { label: string; url: string }[] | null };
  /** Optional (WP stakeholders): second lines of the tiles ("16% of €2.4M", "46% women"). */
  tilesSub?: { spent: string | null; participants: string | null; activities?: string | null };
  audience?: "funder" | "auditor";
  /**
   * AD1 (auditor links only; null for the funder): the financial evidence per
   * budget line ("14. Per diems – Europe"), with what was spent on it and the
   * files to download. Files tied to no budget line come last, under "Not
   * tied to a budget line".
   */
  evidenceByLine?: AuditLine[] | null;
  /**
   * AU-F2, AU-F4 (auditor links only; null for the funder): the evidence that
   * is not a cost of a budget line, each in its own section.
   */
  auditSections?: AuditSections | null;
  /** AU-F4: true when someone of the project team opens the link (their preview); the page shows "This is what the auditor sees" only then. */
  preview?: boolean;
  /** Board 9 (auditor links): the line under the title: "NDICI/2025/451-203 · Balkan Policy Institute · all costs from September 2025 to October 2026, with their proof." */
  auditLead?: string | null;
  /** Board 9: the last day the link works ("31 Mar 2027"); null = no end date. */
  until?: string | null;
  /** Board 9 (auditor links): the latest audit pack (zip) to download through the link; null = no pack made yet. */
  pack?: { url: string; name: string; made: string } | null;
};

/**
 * Board 9: how the auditor's viewer shows a document in place: a PDF or an
 * image as it is; a Word or Excel file as its text (`<url>/preview`); any
 * other file only as a download.
 */
export type AuditDocView = "pdf" | "image" | "word" | "excel" | "other";
/**
 * One file or generated document the auditor can open (through the link's
 * token). Board 9 adds what the viewer says about it: what it proves for the
 * cost ("Receipt", "Quote", "Signed timesheet"), how it opens, its size, who
 * filed it and when.
 */
export type AuditDoc = { label: string; url: string; kind?: string; view?: AuditDocView; size?: string | null; by?: string | null; on?: string | null };
/** Board 9: one check of a cost's proof ("Paid on 12 Jun 2026"), done or missing. */
export type AuditProofCheck = { label: string; done: boolean };
/**
 * AU-F2: one cost booked on a budget line, with the files that prove it
 * (receipts, invoices, signed timesheets). Board 9 adds, for the cost opened
 * in the viewer: its id (a link to the cost), the supplier or claimant, the
 * invoice number, the activity, the proof checks and the trail.
 */
export type AuditCost = {
  title: string;
  date: string;
  amount: string;
  docs: AuditDoc[];
  id?: string;
  supplier?: string | null;
  invoiceNumber?: string | null;
  activity?: string | null;
  proof?: AuditProofCheck[];
  trail?: { date: string; text: string }[];
};
/**
 * AD1, AU-F1/F2/F3: a budget line ("1. Fees key experts"), every line of the
 * budget also when nothing was spent; amounts to the cent; `files`: the
 * line's own files (not of one cost); `costsWithoutFile`: how many costs
 * have no file.
 */
export type AuditLine = {
  line: string;
  spent: string;
  files: AuditDoc[];
  costs?: AuditCost[];
  costsWithoutFile?: number;
  /** Board 9: the funder's heading the line sits under ("Human resources"), for the side list; null = none. */
  heading?: string | null;
  /** Board 9: the line as the funder numbers it ("4.1 Workshops and trainings"). */
  name?: string;
  /** Board 9: what was spent on the line, in cents (the side list adds the lines of a heading). */
  spentCents?: number;
};
export type AuditSections = {
  /** Every expert invoice and claim (fee invoices JM-2026-03 …, not only those sent through the portal). */
  invoices: { number: string; expert: string; period: string; amount: string; status: string; docs: AuditDoc[] }[];
  /** Every approved ToR with the funder's approval (date, Ares reference) and its files. */
  tors: { title: string; approval: string; docs: AuditDoc[] }[];
  /** The contract, its annexes and addenda, the experts' contracts. */
  contract: AuditDoc[];
  /** Proof of the activities: attendance lists, mission reports, photos, per activity. */
  activities: { title: string; docs: AuditDoc[] }[];
  /** Evidence of the results: the indicators' sources of verification, per indicator. */
  results: { title: string; docs: AuditDoc[] }[];
  /** Any other file the auditor may open. */
  other: AuditDoc[];
  /** Board 9 "Expert days": every signed timesheet, with the expert, the month and the days. */
  timesheets?: { expert: string; month: string; days: string; docs: AuditDoc[] }[];
  /** Board 9 "Changes": the contract's amendments sent to the funder, with their decision and files. */
  changes?: { title: string; status: string; docs: AuditDoc[] }[];
};
/** Board 9: a Word or Excel file shown in the auditor's viewer as its text. */
export type AuditDocPreview = { kind: "word"; paragraphs: string[]; more: boolean } | { kind: "excel"; headers: string[]; rows: string[][]; more: boolean };
